import * as React from 'react';
import { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { StoreLocatorEmptyStates } from 'components/store-locator-empty-states';
import { useSendUpdateUserAttributesEventMutation } from 'generated/graphql-gateway';
import { IRestaurantNode } from 'generated/rbi-graphql';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderLocatorTab } from 'state/launchdarkly/variations';

import { LoadingIndicator } from '../../loading-indicator';
import { StoreActions } from '../store-actions';
import { StoreCard } from '../store-card';

import { StoreCardListWrapper, StoreCardWrapper } from './store-card-list.styled';

interface IStoreCardListProps {
  currentTabHasError?: boolean;
  isSearching?: boolean;
  restaurants: IRestaurantNode[];
  tab: OrderLocatorTab;
}

export const StoreCardList: React.FC<IStoreCardListProps> = ({
  currentTabHasError = false,
  isSearching,
  restaurants = [],
  tab,
}) => {
  const { formatMessage } = useIntl();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const { coordinatesAvailable, isPermissionGranted } = useGeolocation();
  const { trackEvent, updateUserAttributes } = useCdpContext();
  const [sendUpdateUserAttributesEvent] = useSendUpdateUserAttributesEventMutation();
  const enableSortedList = useFlag(LaunchDarklyFlag.ENABLE_SORTED_STORE_LOCATION_LIST);

  // sort Stores by isAvailable property
  const sortedStoreList = useMemo(() => {
    if (enableSortedList) {
      return [...restaurants].sort((x, y) => Number(y.isAvailable) - Number(x.isAvailable));
    }

    return restaurants;
  }, [restaurants, enableSortedList]);

  if (enableSortedList) {
    restaurants.sort((x, y) => Number(y.isAvailable) - Number(x.isAvailable));
  }

  const handleCardClick = (index: number) => {
    if (selectedIndex === index) {
      setSelectedIndex(null);
    } else {
      trackEvent({
        name: CustomEventNames.BUTTON_CLICK,
        type: EventTypes.Other,
        attributes: {
          Name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
          StoreId: sortedStoreList[index]._id,
        },
      });
      updateUserAttributes(
        {
          'Sanity Restaurant ID': sortedStoreList[index]._id,
        },
        {},
        sendUpdateUserAttributesEvent
      );

      setSelectedIndex(index);
    }
  };

  if (isSearching) {
    return <LoadingIndicator ariaLabel={formatMessage({ id: 'locationSearch' })} />;
  }

  if (restaurants.length === 0) {
    return (
      <StoreLocatorEmptyStates
        tab={tab}
        locationPermissionsGranted={isPermissionGranted}
        coordinatesAvailable={coordinatesAvailable}
        currentTabError={currentTabHasError}
      />
    );
  }

  return (
    <StoreCardListWrapper>
      {restaurants.map((restaurant, index) => (
        <StoreCardWrapper key={restaurant._id}>
          <StoreCard restaurant={restaurant} onCardClick={() => handleCardClick(index)} />
          {index === selectedIndex ? <StoreActions restaurant={restaurant} /> : null}
        </StoreCardWrapper>
      ))}
    </StoreCardListWrapper>
  );
};
